import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Injector,
  input,
  OnInit,
  output,
  signal,
  TemplateRef,
  untracked,
  viewChild,
} from '@angular/core';
import {
  MASTERDATA_CUSTOMER,
  MasterdataCustomer,
  MasterdataService,
} from '@softapps/core/masterdata';
import { handleRequestError } from '@softline/application';
import {
  clearsManualCustomerAddress,
  MANUAL_CUSTOMER_ADDRESS_RESET,
} from '../../../../helper/document-form/manual-customer-address';
import { cancelableRxResource, debouncedSignal } from '@softline/core';
import {
  BadgeComponent,
  ButtonComponent,
  IconComponent,
  ItemComponent,
  ItemDescriptionComponent,
  ItemLeadingComponent,
  ItemTitleComponent,
  ItemTrailingComponent,
  LoadingSkeletonComponent,
  ResolvableModal,
  SearchInputComponent,
  SoftSlotDirective,
  SOFTLINE_MODAL_RESOLVER,
  TranslatePipe,
} from '@softline/ui-core';
import {
  DocumentFormData,
  EMPTY_DOCUMENT_FORM_DATA,
} from '../../../../types/document-form-data';

function getCustomerAddress(customer: MasterdataCustomer | null): string | null {
  if (!customer) return null;
  return [
    [customer.street, customer.streetNumber].filter((value) => value?.trim()).join(' '),
    [customer.postCode, customer.city].filter((value) => value?.trim()).join(' '),
  ]
    .filter((value) => value?.trim())
    .join('\n');
}

@Component({
  selector: 'soft-sales-customer-step',
  templateUrl: './customer-step.component.html',
  styleUrl: './customer-step.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col h-full min-w-100' },
  imports: [
    TranslatePipe,
    BadgeComponent,
    ItemComponent,
    ItemLeadingComponent,
    ItemTitleComponent,
    ItemDescriptionComponent,
    ItemTrailingComponent,
    IconComponent,
    SearchInputComponent,
    ButtonComponent,
    SoftSlotDirective,
    LoadingSkeletonComponent,
  ],
})
export class CustomerStepComponent
  extends ResolvableModal<DocumentFormData>
  implements OnInit
{
  private readonly modalResolver = inject(SOFTLINE_MODAL_RESOLVER, { optional: true });

  private readonly masterdata = inject(MasterdataService);

  override readonly bottomTemplate = viewChild('bottomTpl', { read: TemplateRef });

  readonly data = input.required<DocumentFormData>();
  readonly isEmbedded = input(false);
  // One-time-seeded local copy. Do NOT resync from `data()` — see
  // construction-site-step for the reason (parent writebacks clobber
  // step-local edits). Seed in ngOnInit; reading `data()` during field
  // initialization throws NG0950 because required inputs aren't bound yet.
  readonly local = signal<DocumentFormData>(EMPTY_DOCUMENT_FORM_DATA);
  readonly dataChange = output<DocumentFormData>();
  readonly next = output<DocumentFormData>();
  readonly create = output<DocumentFormData>();

  readonly search = signal('');
  private readonly debouncedSearch = debouncedSignal(this.search, 200);
  readonly skeletonRows = Array.from({ length: 6 });

  private readonly injector = inject(Injector);
  private readonly pageSize = 100;

  private readonly customersResource = cancelableRxResource({
    params: () => ({ filter: this.debouncedSearch().trim() }),
    stream: ({ params }) =>
      this.masterdata.search$(MASTERDATA_CUSTOMER, params.filter, {
        limit: this.pageSize,
      }),
  });

  /** Pages beyond the first, loaded via offset when the list is scrolled to
      the end. Tagged with their filter so a new search discards them. */
  private readonly extraPages = signal<{
    filter: string;
    items: MasterdataCustomer[];
    lastBatchFull: boolean;
  }>({ filter: '', items: [], lastBatchFull: true });

  readonly loadingMore = signal(false);

  private readonly currentFilter = computed(() => this.debouncedSearch().trim());

  readonly customers = computed<MasterdataCustomer[]>(() => {
    const base = this.customersResource.value() ?? [];
    const extra = this.extraPages();
    if (extra.filter !== this.currentFilter() || extra.items.length === 0) return base;
    const seen = new Set<string>();
    return [...base, ...extra.items].filter((c) => {
      const key = this.rowKey(c);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  });

  readonly hasMore = computed(() => {
    const base = this.customersResource.value() ?? [];
    if (base.length < this.pageSize) return false;
    const extra = this.extraPages();
    if (extra.filter !== this.currentFilter() || extra.items.length === 0) return true;
    return extra.lastBatchFull;
  });

  readonly loading = computed(() => this.customersResource.status() === 'loading');

  async loadMore(): Promise<void> {
    if (this.loadingMore() || this.loading() || !this.hasMore()) return;
    const filter = this.currentFilter();
    const offset = this.customers().length;
    this.loadingMore.set(true);
    try {
      const batch = await this.masterdata.search(MASTERDATA_CUSTOMER, filter, {
        limit: this.pageSize,
        offset,
      });
      this.extraPages.update((prev) => ({
        filter,
        items: prev.filter === filter ? [...prev.items, ...(batch ?? [])] : (batch ?? []),
        lastBatchFull: (batch?.length ?? 0) === this.pageSize,
      }));
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    } finally {
      this.loadingMore.set(false);
    }
  }

  onListScroll(event: Event): void {
    const el = event.target as HTMLElement | null;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
      void this.loadMore();
    }
  }

  protected readonly selectedKey = computed(() => {
    const c = this.local().customer;
    return c ? `${c.customerNumber}/${c.customerSubnumber ?? 0}` : null;
  });

  rowKey(c: MasterdataCustomer): string {
    return `${c.customerNumber}/${c.customerSubnumber ?? 0}`;
  }

  rowInitials(c: MasterdataCustomer): string {
    const initials = [c.name1, c.name2, c.name3]
      .filter((n): n is string => !!n?.trim())
      .map((n) => n.trim()[0].toUpperCase())
      .slice(0, 2)
      .join('');
    if (initials) return initials;
    const fallback = (c.shortName || '').trim();
    return fallback ? fallback[0].toUpperCase() : '?';
  }

  rowName(c: MasterdataCustomer): string {
    return (
      [c.name1, c.name2, c.name3]
        .filter((n) => n?.trim())
        .join(' ')
        .trim() ||
      c.shortName ||
      '—'
    );
  }

  rowAddress(c: MasterdataCustomer): string | null {
    const street = [c.street, c.streetNumber]
      .filter((v) => v?.trim())
      .join(' ')
      .trim();
    const cityLine = [c.postCode, c.city]
      .filter((v) => v?.trim())
      .join(' ')
      .trim();
    const result = [street, cityLine].filter(Boolean).join(', ');
    return result.length > 0 ? result : null;
  }

  rowNumberLabel(c: MasterdataCustomer): string {
    return c.customerSubnumber && c.customerSubnumber > 0
      ? `· ${c.customerNumber}/${c.customerSubnumber}`
      : `· ${c.customerNumber}`;
  }

  ngOnInit(): void {
    this.local.set(untracked(() => this.data()));
  }

  selectCustomer(customer: MasterdataCustomer): void {
    this.local.update((d) => ({
      ...d,
      customer,
      manualAddress: getCustomerAddress(customer),
      // The wizard's own effect only clears when switching to a REGULAR customer;
      // between two Barverkauf accounts the previous walk-in would survive.
      ...(clearsManualCustomerAddress(d.customer, customer)
        ? MANUAL_CUSTOMER_ADDRESS_RESET
        : {}),
      // A Baustelle is a Sub-Kunde of the previously picked customer — another
      // customer (or a Barverkauf account, which never has Baustellen)
      // invalidates it, so the wizard cannot submit a stale one.
      ...(d.customer?.id !== customer.id || customer.oneTimeCustomer
        ? {
            constructionSiteId: null,
            constructionSite: null,
            constructionSiteName: null,
            constructionSiteAddress: null,
            manualConstructionSiteEnabled: false,
          }
        : {}),
    }));
    this.dataChange.emit(this.local());
    if (!this.isEmbedded()) {
      this.confirm();
    }
  }

  confirm(): void {
    const result = this.local();
    if (this.modalResolver && !this.isEmbedded()) {
      this.resolve(result);
    } else {
      this.next.emit(result);
    }
  }
}
